import { useEffect, useRef, useState, type RefObject } from 'react'

import { ColumnOrderChooser, type WarehouseColumnId } from '@/components/views/ColumnOrderChooser'
import type { UnitLifecycleScope } from '@/lib/api/schemas/inventory.schema'
import { warehouseProductStatusLabels } from '@/lib/warehouse/labels'
import { groupingsFor, type WarehouseGrouping } from '@/lib/warehouse/url-state'
import type { ViewPresetKey } from '@/lib/views/presets'

const groupingLabels: Record<WarehouseGrouping, [string, string]> = {
  none: ['Без группировки', 'один товар'],
  model: ['По модели', 'все артикулы модели'],
  article: ['По артикулу', 'все размеры артикула'],
  articleSize: ['По артикулу и размеру', 'товары одного размера'],
  order: ['По заказу', 'изделия одного заказа'],
}

export const lifecycleLabels: Record<UnitLifecycleScope, string> = {
  all: 'Все статусы',
  in_stock: warehouseProductStatusLabels.in_stock,
  in_transit: warehouseProductStatusLabels.in_transit,
  sold: warehouseProductStatusLabels.sold,
}

export function WarehouseToolbar({
  search,
  loading = false,
  grouping,
  withPhotos,
  withPhotosCount,
  modelLevelAvailable,
  filterCount,
  filtersOpen,
  filterTriggerRef,
  onSearch,
  onGrouping,
  onWithPhotos,
  onFiltersOpen,
  visibleColumns,
  onVisibleColumnsChange,
  onVisibleColumnsReset,
  columnsSaving = false,
  className = 'mt-4',
  view = 'actual',
}: {
  search: string
  loading?: boolean
  grouping: WarehouseGrouping
  lifecycle?: UnitLifecycleScope
  withPhotos: boolean
  withPhotosCount: number | null
  modelLevelAvailable: boolean
  filterCount: number
  filtersOpen: boolean
  filterTriggerRef: RefObject<HTMLButtonElement | null>
  onSearch: (value: string) => void
  onGrouping: (value: WarehouseGrouping) => void
  onLifecycle?: (value: UnitLifecycleScope) => void
  onWithPhotos: (value: boolean) => void
  onFiltersOpen: () => void
  visibleColumns?: readonly WarehouseColumnId[]
  onVisibleColumnsChange?: (columns: readonly WarehouseColumnId[]) => void
  onVisibleColumnsReset?: () => void
  columnsSaving?: boolean
  className?: string
  view?: ViewPresetKey
}) {
  const [draft, setDraft] = useState(search)
  const callback = useRef(onSearch)
  callback.current = onSearch
  useEffect(() => setDraft(search), [search])
  useEffect(() => {
    if (draft === search) return
    const timer = setTimeout(() => callback.current(draft.trim()), 300)
    return () => clearTimeout(timer)
  }, [draft, search])

  return (
    <>
      <section aria-label="Панель склада" className={`${className} flex flex-wrap items-center gap-2 rounded-lg border border-rule bg-paper p-2.5`}>
        <div className="flex min-w-[220px] flex-1 items-center gap-2">
          <input aria-label="Артикул, модель, SKU…" value={draft} onChange={(event) => setDraft(event.target.value)} placeholder="Артикул, модель, SKU… (от 3 символов; короче — точный УИН или номер заказа)" className="h-9 min-w-0 flex-1 rounded-md border border-rule px-3 text-xs" />
          {loading ? <span role="status" aria-label="Поиск выполняется" data-testid="warehouse-search-spinner" className="inline-block size-3 shrink-0 animate-spin rounded-full border border-rule-strong border-t-gold-deep motion-reduce:animate-none" /> : null}
        </div>
        <label className="sr-only" htmlFor="warehouse-grouping">Группировка</label>
        <select id="warehouse-grouping" aria-label="Группировка" value={grouping} onChange={(event) => onGrouping(event.target.value as WarehouseGrouping)} className="h-9 rounded-md border border-rule bg-white px-3 text-xs">
          {groupingsFor(view).map((value) => (
            <option key={value} value={value} disabled={value === 'model' && !modelLevelAvailable}>{groupingLabels[value][0]} · {value === 'model' && !modelLevelAvailable ? 'нет моделей в выборке' : groupingLabels[value][1]}</option>
          ))}
        </select>
        <button type="button" aria-pressed={withPhotos} onClick={() => onWithPhotos(!withPhotos)} className={`h-9 rounded-full border px-3 text-xs ${withPhotos ? 'border-gold-deep bg-gold/10 text-gold-deep' : 'border-rule text-ink-2'}`}>
          Только с фото <span className="ml-1 rounded-full bg-bone px-1.5">{withPhotosCount === null ? '…' : view === 'orders' ? `${withPhotosCount} изд.` : withPhotosCount}</span>
        </button>
        {visibleColumns && onVisibleColumnsChange ? <ColumnOrderChooser columns={visibleColumns} grouping={grouping} onChange={onVisibleColumnsChange} onReset={onVisibleColumnsReset} saving={columnsSaving} /> : null}
        <button
          ref={filterTriggerRef}
          type="button"
          aria-expanded={filtersOpen}
          aria-controls="warehouse-filter-surface"
          onClick={onFiltersOpen}
          className="ml-auto h-9 rounded-full border border-rule px-3 text-xs font-medium text-ink-2 hover:bg-bone/60"
        >
          Фильтры <span className="ml-1 rounded-full bg-gold-deep px-1.5 py-0.5 text-white">{filterCount}</span>
        </button>
      </section>
    </>
  )
}
